import React, { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import logger from '../../utils/logger';
import { useAuth } from '../../context/AuthContext';
import { API, axiosInstance } from './dashboardApi';

export const useConsultantSessionRequests = () => {
  const { user } = useAuth();
  const [requestingSession, setRequestingSession] = useState(null);
  const [hasPendingAssignmentRequest, setHasPendingAssignmentRequest] = useState(false);
  const [checkingAssignmentRequest, setCheckingAssignmentRequest] = useState(true);
  const [hasConsultant, setHasConsultant] = useState(user?.consultant_id ? true : false);
  const [previousConsultantState, setPreviousConsultantState] = useState(user?.consultant_id ? true : false);
  const toastShownRef = useRef(false); // Track if we've shown the toast to prevent duplicates
  const lastConsultantIdRef = useRef(user?.consultant_id || null); // Track the last consultant_id we've seen
  const initialLoadRef = useRef(true); // Track if this is the initial load
  
  // Initialize lastConsultantIdRef with user's consultant_id if available
  if (user?.consultant_id && !lastConsultantIdRef.current) {
    lastConsultantIdRef.current = user.consultant_id;
  }

  const requestConsultantAssignment = async () => {
    try {
      toast.loading('Submitting your request...', { id: 'assignment-request' });

      const response = await axiosInstance.post(`${API}/user/consultant-assignment-request`, {
        notes: ''
      });

      logger.log('Assignment request response:', response.data.success);

      if (response.data && response.data.success === true) {
        setHasPendingAssignmentRequest(true);

        toast.success(
          '✅ Your request has been sent to the admin! They will assign a consultant to your account soon.',
          {
            id: 'assignment-request',
            duration: 6000
          }
        );
      } else {
        const errorMsg =
          response.data?.message ||
          response.data?.error ||
          'Failed to submit request';
        toast.error(errorMsg, { id: 'assignment-request', duration: 6000 });
      }
    } catch (error) {
      logger.error('Failed to submit assignment request:', error);

      let errorMsg = 'Failed to submit assignment request. Please try again.';

      if (error.response) {
        logger.log('Error response:', error.response.data);

        if (error.response.status === 409) {
          setHasPendingAssignmentRequest(true);
          errorMsg =
            error.response.data?.message ||
            error.response.data?.detail ||
            'You already have a pending consultant assignment request. Please wait for the administrator to process it.';
        } else if (error.response.status === 400) {
          errorMsg =
            error.response.data?.message ||
            error.response.data?.detail ||
            'Bad request. Please try again.';
        } else {
          errorMsg =
            error.response.data?.message ||
            error.response.data?.detail ||
            error.response.data?.error ||
            `Request failed with status ${error.response.status}`;
        }
      } else if (error.request) {
        errorMsg = 'No response from server. Please check your connection.';
      } else {
        errorMsg = error.message || errorMsg;
      }

      toast.error(errorMsg, { id: 'assignment-request', duration: 6000 });
    }
  };

  const handleNoConsultantAssigned = async () => {
    const result = await Swal.fire({
      title: 'No Consultant Assigned',
      html: `
        <p class="text-gray-600 mb-4">A consultant has not been assigned to your account yet.</p>
        <p class="text-sm text-gray-500 mb-2">You can:</p>
        <ul class="text-sm text-left text-gray-600 mb-4 list-disc pl-6">
          <li>Request a consultant assignment (we'll notify the admin)</li>
          <li>Or proceed with a self-guided assessment</li>
        </ul>
      `,
      icon: 'info',
      showCancelButton: true,
      confirmButtonText: 'Request Consultant Assignment',
      cancelButtonText: 'Close',
      confirmButtonColor: '#10b981',
      cancelButtonColor: '#6b7280',
      reverseButtons: true
    });

    if (result.isConfirmed) {
      await requestConsultantAssignment();
    }
  };

  const checkConsultantAssignment = async () => {
    try {
      // Actually query the backend to check if consultant is assigned
      // We'll check by attempting to validate a session request (lightweight check)
      // Or by checking user data if available
      let currentHasConsultant = false;
      
      // Method 1: Check user context first (fast, but might be stale)
      if (user?.consultant_id) {
        currentHasConsultant = true;
        logger.debug('✅ Consultant found in user context:', user.consultant_id);
      } else {
        // Method 2: Query backend directly by checking if we can make a session request
        // We'll make a lightweight HEAD/OPTIONS check or try to get user info
        // Actually, the best way is to check by trying to validate session request
        // But that's too heavy. Instead, let's check the pending assignment request
        // and see if it's been resolved (which means consultant was assigned)
        
        // Check if pending request still exists - if it doesn't and we had one, consultant might be assigned
        try {
          const pendingResponse = await axiosInstance.get(`${API}/user/pending-assignment-request`);
          const stillPending = pendingResponse.data?.has_pending_request === true;
          
          // If we had a pending request but it's no longer pending, consultant might be assigned
          // But we need to verify by actually checking consultant_id
          if (hasPendingAssignmentRequest && !stillPending) {
            // Request was resolved - check if consultant is now assigned
            // We'll verify by checking user context or making another check
            logger.debug('Pending request was resolved, checking if consultant is assigned...');
          }
        } catch (pendingErr) {
          logger.debug('Error checking pending request:', pendingErr);
        }
        
        // Method 3: Try to verify by checking if user can access consultant-required functionality
        // The simplest is to check user context, but if it's not updated, we need backend check
        // For now, if user context doesn't have consultant_id, check current state
        currentHasConsultant = hasConsultant;
        logger.debug('Consultant not in user context, using current state:', hasConsultant);
      }
      
      // CRITICAL: Also check user context for updates (in case it was refreshed)
      // This catches cases where user context was updated but we haven't detected it
      if (user?.consultant_id && !currentHasConsultant) {
        currentHasConsultant = true;
        logger.debug('✅ Consultant found in user context (updated):', user.consultant_id);
      }
      
      // Update state if consultant assignment changed
      if (currentHasConsultant !== previousConsultantState) {
        const wasAssigned = currentHasConsultant && !previousConsultantState;
        logger.log(`🔄 Consultant assignment status changed: ${previousConsultantState} -> ${currentHasConsultant}`);
        setHasConsultant(currentHasConsultant);
        setPreviousConsultantState(currentHasConsultant);
        
        // If consultant was just assigned, show notification and clear pending request
        if (wasAssigned) {
          logger.log('✅ Consultant was just assigned! Showing notification and updating UI.');
          toast.success(' A consultant has been assigned to your account! You can now request live sessions.', {
            duration: 5000
          });
          setHasPendingAssignmentRequest(false);
        }
      }
      
      return currentHasConsultant;
    } catch (error) {
      logger.debug('Error checking consultant assignment:', error);
      return hasConsultant; // Return current state on error
    }
  };

  const checkPendingAssignmentRequest = async () => {
    try {
      setCheckingAssignmentRequest(true);
      logger.log('🔍 Checking for pending assignment request...');

      const response = await axiosInstance.get(`${API}/user/pending-assignment-request`);

      logger.log('Pending assignment request response:', response.data);

      const stillPending = response.data && response.data.has_pending_request === true;
      const hasConsultantFromBackend = response.data?.has_consultant === true;
      const consultantIdFromBackend = response.data?.consultant_id;
      
      // Update pending request state
      if (stillPending) {
        logger.log('✅ Found pending assignment request');
        setHasPendingAssignmentRequest(true);
      } else {
        logger.log('❌ No pending assignment request found');
        setHasPendingAssignmentRequest(false);
      }
      
      // Check consultant assignment from backend response (real-time check)
      if (hasConsultantFromBackend && consultantIdFromBackend) {
        const previousConsultantId = lastConsultantIdRef.current;
        const consultantIdChanged = previousConsultantId !== consultantIdFromBackend;
        const wasJustAssigned = !previousConsultantId && consultantIdFromBackend; // Only true if consultant_id changed from null/undefined to a value
        const hadPendingRequest = hasPendingAssignmentRequest; // Capture before clearing
        
        logger.log(`✅ Consultant found in backend response: ${consultantIdFromBackend}, previous: ${previousConsultantId}, wasJustAssigned: ${wasJustAssigned}, hadPendingRequest: ${hadPendingRequest}, initialLoad: ${initialLoadRef.current}`);
        
        // Always update state if backend says consultant is assigned
        const needsStateUpdate = !hasConsultant || consultantIdChanged;
        
        if (needsStateUpdate) {
          logger.log('🔄 Consultant assignment detected from backend! Updating state.');
          
          // Update consultant ID ref first
          lastConsultantIdRef.current = consultantIdFromBackend;
          
          // Only show toast if:
          // 1. This is NOT the initial load (to prevent showing on refresh/login)
          // 2. Consultant was just assigned (changed from null to a value)
          // 3. There was a pending request (meaning user just requested and got assigned)
          // 4. We haven't shown the toast yet for this assignment
          const shouldShowToast = !initialLoadRef.current && wasJustAssigned && hadPendingRequest && !toastShownRef.current;
          
          // Batch all state updates together in one synchronous update to prevent flickering
          // React 18+ automatically batches these, but we ensure they happen together
          setHasConsultant(true);
          setPreviousConsultantState(true);
          setHasPendingAssignmentRequest(false);
          
          if (shouldShowToast) {
            logger.log('🎉 Showing success notification for consultant assignment (new assignment detected)');
            toastShownRef.current = true;
            
            // Use a small delay to ensure state updates are rendered first
            setTimeout(() => {
              toast.success(' A consultant has been assigned to your account! You can now request live sessions.', {
                duration: 5000,
                id: 'consultant-assigned',
                position: 'top-center',
                style: {
                  background: '#10b981',
                  color: '#fff',
                },
                icon: '🎉'
              });
            }, 50);
          }
        } else {
          // Even if state doesn't need update, clear pending request if consultant is assigned
          setHasPendingAssignmentRequest(prev => {
            if (prev) {
              logger.log('✅ Clearing pending assignment request - consultant is already assigned');
              return false;
            }
            return prev;
          });
        }
      } else if (!hasConsultantFromBackend && hasConsultant) {
        // Consultant was removed
        logger.log('⚠️ Consultant was removed according to backend');
        setHasConsultant(false);
        setPreviousConsultantState(false);
        lastConsultantIdRef.current = null;
      } else if (!hasConsultantFromBackend && hasPendingAssignmentRequest) {
        // Still no consultant, but we have a pending request - that's expected
        logger.debug('Still waiting for consultant assignment...');
      }
      
      // Also check consultant assignment status via other method
      await checkConsultantAssignment();
    } catch (error) {
      logger.error('Error checking pending assignment request:', error);
      logger.log('⚠️ Error occurred, resetting state to false');
      setHasPendingAssignmentRequest(false);
    } finally {
      setCheckingAssignmentRequest(false);
    }
  };

  const handleRequestConsultantSession = async (assessmentType) => {
    if (requestingSession) return;

    // Don't block the request based on frontend state - let backend validate
    // The backend will return appropriate error if no consultant is assigned
    // This ensures we don't incorrectly block when consultant is assigned but context is stale
    // Only show modal if we get a "no consultant" error from backend
    
    setRequestingSession(assessmentType);
    toast.loading(`Requesting ${assessmentType} session...`, { id: 'request-session' });

    try {
      const response = await axiosInstance.post(`${API}/live-session/request`, {
        assessment_type: assessmentType
      });

      if (response.data.success) {
        toast.success('Session request sent! Your consultant will be notified.', {
          id: 'request-session'
        });
        // Trigger a custom event to refresh live session data without page reload
        window.dispatchEvent(new CustomEvent('refreshLiveSession'));
      } else {
        const errorMessage =
          response.data.message || response.data.error || 'Failed to request session.';

        if (
          errorMessage.includes("don't have an assigned consultant") ||
          errorMessage.includes('consultant assigned') ||
          errorMessage.includes('no assigned consultant')
        ) {
          toast.dismiss('request-session');
          await handleNoConsultantAssigned();
        } else {
          toast.error(errorMessage, { id: 'request-session' });
        }
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.detail ||
        error.response?.data?.error ||
        error.message ||
        'Failed to request session. Please try again.';

      // Check if this is the expected "no consultant assigned" error
      const errorMessageLower = errorMessage.toLowerCase();
      const isNoConsultantError =
        errorMessage.includes("don't have an assigned consultant") ||
        errorMessage.includes("don't have an assigned consultant yet") ||
        errorMessage.includes('consultant assigned') ||
        errorMessage.includes('no assigned consultant') ||
        errorMessage.includes('No consultant assigned') ||
        errorMessage.includes('consultant not assigned') ||
        errorMessageLower.includes('assigned consultant yet') ||
        (error.response?.status === 400 && (
          errorMessageLower.includes('consultant') && 
          (errorMessageLower.includes('assigned') || errorMessageLower.includes('assignment'))
        ));

      if (isNoConsultantError) {
        // This is expected behavior - don't log as error, just show the modal
        logger.debug('User has no consultant assigned, showing assignment request modal');
        toast.dismiss('request-session');
        await handleNoConsultantAssigned();
      } else {
        // This is an unexpected error - log it
        logger.error('❌ Failed to request session:', error);
        toast.error(errorMessage, { id: 'request-session' });
      }
    } finally {
      setRequestingSession(null);
    }
  };

  useEffect(() => {
    // Mark initial load as complete after first check
    checkPendingAssignmentRequest().then(() => {
      // If user already has a consultant, mark initial load as complete immediately
      // Otherwise wait a bit to ensure initial state is set
      if (user?.consultant_id || lastConsultantIdRef.current) {
        initialLoadRef.current = false;
        logger.log('✅ Initial load complete (user already has consultant) - toast will now show for new assignments only');
      } else {
        // Wait a bit to ensure initial state is set, then mark initial load as complete
        setTimeout(() => {
          initialLoadRef.current = false;
          logger.log('✅ Initial load complete - toast will now show for new assignments only');
        }, 1500);
      }
    }).catch(() => {
      // Even on error, mark initial load as complete
      setTimeout(() => {
        initialLoadRef.current = false;
      }, 1500);
    });
  }, [user?.consultant_id]);
  
  // Poll for consultant assignment every 5 seconds if user has pending request or no consultant
  useEffect(() => {
    if (!hasPendingAssignmentRequest && hasConsultant) {
      // Don't poll if consultant is already assigned and no pending request
      logger.debug('Stopping polling - consultant assigned and no pending request');
      return;
    }
    
    logger.debug('Starting polling for consultant assignment...');
    
    // Poll for consultant assignment every 5 seconds
    // Call checkPendingAssignmentRequest which queries the backend for both pending request and consultant status
    const pollInterval = setInterval(async () => {
      logger.debug('Polling: Checking for consultant assignment...');
      await checkPendingAssignmentRequest();
    }, 5000);
    
    return () => {
      logger.debug('Cleaning up polling interval');
      clearInterval(pollInterval);
    };
  }, [hasPendingAssignmentRequest, hasConsultant]);
  
  // Check when user object changes - this catches updates from auth context
  useEffect(() => {
    const currentConsultantId = user?.consultant_id;
    const hadConsultantBefore = hasConsultant;
    
    if (currentConsultantId && !hadConsultantBefore) {
      // Consultant was just assigned
      setHasConsultant(true);
      setPreviousConsultantState(true);
      if (hasPendingAssignmentRequest) {
        toast.success(' A consultant has been assigned to your account! You can now request live sessions.', {
          duration: 5000
        });
        setHasPendingAssignmentRequest(false);
      }
    } else if (!currentConsultantId && hadConsultantBefore) {
      // Consultant was removed
      setHasConsultant(false);
      setPreviousConsultantState(false);
    } else if (currentConsultantId && hadConsultantBefore) {
      // Consultant still assigned - ensure state is correct
      setHasConsultant(true);
    } else if (!currentConsultantId && !hadConsultantBefore) {
      // Still no consultant - ensure state is correct
      setHasConsultant(false);
    }
  }, [user?.consultant_id, hasConsultant, hasPendingAssignmentRequest]);

  return {
    requestingSession,
    hasPendingAssignmentRequest,
    checkingAssignmentRequest,
    handleRequestConsultantSession,
    hasConsultant: hasConsultant || (user?.consultant_id ? true : false)
  };
};



